import { useEffect, useMemo, useState } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { formatDateToDDMMYYYY } from '../../utils';

const stickyCellStyle = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

interface PreviewInvoice {
  id: number;
  deal_id: number;
  reg_plate: string;
  selling_price: string;
  pipeline_letter: string;
  vat_rate: string;
  created_at: string;
  pipeline_source: string;
  is_late?: boolean;
}

interface PreviewData {
  client_id: string;
  invoicing_type: string;
  agency_name: string;
  agency_id: number;
  cg_fee: number;
  da_fee: number;
  invoices: PreviewInvoice[];
}

const CGNextPeriodInvoicesPreview = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);

  const fetchPreviewData = async () => {
    setIsLoading(true);
    try {
      const response = await makeAuthenticatedApiCall('get', '/api/qonto/next-period-preview-invoices');
      setPreviewData((response.data as PreviewData[]) || []);
    } catch (error) {
      console.error('Error fetching next period preview data:', error);
    }
    setIsLoading(false);
  };

  // Calculate the NEXT billing period based on invoicing type
  const calculateNextBillingPeriod = (invoicingType: string): string => {
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    const monthNames = [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ];

    if (invoicingType === 'Monthly') {
      // Current month will be invoiced next month
      return `${monthNames[currentMonth - 1]} ${currentYear}`;
    } else if (invoicingType === 'Bi-weekly') {
      if (currentDay >= 15) {
        // 15-end of current month, will be invoiced on the 1st of next month
        const lastDayOfMonth = new Date(currentYear, currentMonth, 0).getDate();
        return `15 - ${lastDayOfMonth} ${monthNames[currentMonth - 1]} ${currentYear}`;
      } else {
        // 1-14 of current month, will be invoiced on the 15th
        return `1 - 14 ${monthNames[currentMonth - 1]} ${currentYear}`;
      }
    }
    return '';
  };

  // Calculate when the invoices will be available for generation
  const calculateNextGenerationDate = (invoicingType: string): string => {
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    if (invoicingType === 'Monthly') {
      // Next month, 1st day
      const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      return `1er ${['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][nextMonth]} ${nextYear}`;
    } else if (invoicingType === 'Bi-weekly') {
      if (currentDay >= 15) {
        // Next month, 1st day
        const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
        const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
        return `1er ${['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][nextMonth]} ${nextYear}`;
      } else {
        // 15th of current month
        return `15 ${['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][currentMonth]} ${currentYear}`;
      }
    }
    return '';
  };

  useEffect(() => {
    fetchPreviewData();
  }, []);

  // Group preview data by agency
  const groupedByAgency = useMemo(() => {
    const groups = new Map<number, { agency_name: string; clients: PreviewData[] }>();

    previewData.forEach((item) => {
      const agencyId = item.agency_id;
      if (!groups.has(agencyId)) {
        groups.set(agencyId, {
          agency_name: item.agency_name,
          clients: [],
        });
      }
      groups.get(agencyId)!.clients.push(item);
    });

    return Array.from(groups.entries()).map(([agencyId, data]) => ({
      agencyId,
      ...data,
    }));
  }, [previewData]);

  // Define alternating colors from theme (slightly different hue for distinction)
  const agencyColors = [
    'rgba(149, 225, 211, 0.3)', // teal
    'rgba(168, 230, 207, 0.3)', // mint
    'rgba(186, 220, 188, 0.3)', // sage
    'rgba(200, 230, 201, 0.3)', // light green
  ];

  if (isLoading) {
    return (
      <Grid container direction="column" justifyContent="center" alignItems="center" marginTop={2}>
        <Typography sx={{ marginBottom: 2 }} variant="body1">
          Chargement
        </Typography>
        <CircularProgress size={70} />
      </Grid>
    );
  }

  if (previewData.length === 0) {
    return (
      <Container maxWidth={false}>
        <Typography variant="h4" gutterBottom sx={{ marginTop: 4 }}>
          Aperçu prochaine facturation
        </Typography>
        <Paper sx={{ marginTop: 5, padding: 4 }} elevation={3}>
          <Typography variant="body1" align="center">
            Aucune facture en cours d'accumulation pour la prochaine période.
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 2 }}>
            Les factures apparaîtront ici au fur et à mesure que les dossiers seront traités (réception du CTVO pour les
            CG, récépissé pour les DA).
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ marginTop: 4 }}>
          Aperçu prochaine facturation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Voici les factures en cours d'accumulation pour la prochaine période de facturation.
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            mb: 3,
            borderRadius: 2,
            backgroundColor: 'rgba(149, 225, 211, 0.2)',
            border: '1px solid rgba(149, 225, 211, 0.5)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Ces factures ne peuvent pas encore être générées. Elles seront disponibles à l'ouverture de la prochaine
            période.
          </Typography>
        </Box>
      </Box>

      {groupedByAgency.map((agencyGroup, agencyIndex) => (
        <Paper
          key={agencyGroup.agencyId}
          sx={{
            mb: 4,
            p: 2,
            backgroundColor: agencyColors[agencyIndex % agencyColors.length],
            borderRadius: 2,
          }}
          elevation={3}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#262626' }}>
            {agencyGroup.agency_name}
          </Typography>

          {agencyGroup.clients.map((client) => {
            const cgInvoices = client.invoices.filter((inv) => inv.pipeline_source === 'f');
            const daInvoices = client.invoices.filter((inv) => inv.pipeline_source === 'da');
            const cgCount = cgInvoices.length;
            const daCount = daInvoices.length;

            // Calculate amounts
            const cgTotalHT = cgInvoices.reduce((sum, inv) => sum + parseFloat(inv.selling_price), 0);
            const cgFee = client.cg_fee || 10.0;
            const daFee = client.da_fee || 10.0;
            const daTotalHT = daCount * daFee;
            const commissionsHT = cgCount * cgFee;
            const commissionsWithVAT = commissionsHT * 1.2;
            const daWithVAT = daTotalHT * 1.2;
            const totalTTC = cgTotalHT + daWithVAT + commissionsWithVAT;

            const billingPeriod = calculateNextBillingPeriod(client.invoicing_type);
            const generationDate = calculateNextGenerationDate(client.invoicing_type);

            return (
              <Box key={client.client_id} sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#464646' }}>
                      Client {client.client_id} - Type de facturation: {client.invoicing_type}
                    </Typography>
                    <Box display="flex" gap={1} sx={{ mb: 1, mt: 0.5 }}>
                      <Chip
                        label={`Période: ${billingPeriod}`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(100, 181, 246, 0.2)',
                          color: '#1565c0',
                          fontWeight: 500,
                        }}
                      />
                      <Chip
                        label={`Disponible le: ${generationDate}`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255, 183, 77, 0.2)',
                          color: '#e65100',
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Total estimé TTC : {totalTTC.toFixed(2)} €
                    </Typography>
                    {cgCount > 0 && (
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
                        • {cgCount} CG : {cgTotalHT.toFixed(2)} € HT + commissions ({cgCount} × {cgFee.toFixed(2)} €) :{' '}
                        {commissionsHT.toFixed(2)} € HT = {(cgTotalHT + commissionsWithVAT).toFixed(2)} € TTC
                      </Typography>
                    )}
                    {daCount > 0 && (
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#666' }}>
                        • {daCount} DA : {daCount} × {daFee.toFixed(2)} € = {daTotalHT.toFixed(2)} € HT + TVA (20%) ={' '}
                        {daWithVAT.toFixed(2)} € TTC
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Chip
                      label="En attente de période"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(158, 158, 158, 0.2)',
                        color: '#616161',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={stickyCellStyle}>Immatriculation</TableCell>
                        <TableCell sx={stickyCellStyle}>Deal ID</TableCell>
                        <TableCell sx={stickyCellStyle}>Prix</TableCell>
                        <TableCell sx={stickyCellStyle}>Type</TableCell>
                        <TableCell sx={stickyCellStyle}>TVA</TableCell>
                        <TableCell sx={stickyCellStyle}>Date de création</TableCell>
                        <TableCell sx={stickyCellStyle}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {client.invoices.map((invoice) => (
                        <TableRow
                          key={invoice.id}
                          sx={{
                            backgroundColor: invoice.is_late
                              ? 'rgba(255, 152, 0, 0.08)'
                              : 'rgba(33, 150, 243, 0.05)',
                          }}
                        >
                          <TableCell>{invoice.reg_plate}</TableCell>
                          <TableCell>{invoice.deal_id}</TableCell>
                          <TableCell>{parseFloat(invoice.selling_price).toFixed(2)} €</TableCell>
                          <TableCell>{invoice.pipeline_letter.toUpperCase()}</TableCell>
                          <TableCell>{invoice.vat_rate}</TableCell>
                          <TableCell>{formatDateToDDMMYYYY(invoice.created_at)}</TableCell>
                          <TableCell>
                            {invoice.is_late ? (
                              <Tooltip
                                title="Ce deal provient d'une période antérieure. Il n'a pas pu être facturé à temps car le récépissé (CTVO ou DA) n'était pas encore disponible. Il sera inclus dans la prochaine génération."
                                arrow
                                placement="left"
                              >
                                <InfoOutlinedIcon
                                  sx={{
                                    color: '#ff9800',
                                    fontSize: 20,
                                    cursor: 'help',
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip
                                title="Ce deal appartient à la période en cours. Il sera facturé à l'ouverture de la prochaine période de facturation."
                                arrow
                                placement="left"
                              >
                                <CheckCircleOutlineIcon
                                  sx={{
                                    color: '#2196f3',
                                    fontSize: 20,
                                    cursor: 'help',
                                  }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </Paper>
      ))}
    </Container>
  );
};

export default CGNextPeriodInvoicesPreview;
